/*
  Rigging add-on — puts the chosen rig into the cart as a SECOND line item
  alongside the lure, plus a THIRD line for the mandatory fitting fee.

  WHY A SECOND LINE ITEM AND NOT A VARIANT
  ----------------------------------------
  A Shopify line item decrements exactly one inventory item; there is no
  bill-of-materials. While rigging was an option on the lure, buying a rigged
  lure moved the lure's stock and never touched the rig's, so the rig counts
  drifted with every sale. Sending the rig as its own line item makes its
  inventory decrement because it IS the thing bought.

  HOW IT HOOKS THE FORM
  ---------------------
  The product form is left to do its own thing. On submit we cancel, POST every
  item to /cart/add.js in ONE call, then hand control back. One call matters:
  two sequential POSTs can half-succeed and leave a lure in the cart with no
  rig, which is worse than failing outright.

  The radiogroup uses a roving tabindex — the same pattern as the tuna
  configurator's option controls — so arrow keys move between rigs and Tab
  leaves the group.
*/
(function () {
  'use strict';

  function init(root) {
    if (root.dataset.rigReady === '1') return;
    root.dataset.rigReady = '1';

    var types  = [].slice.call(root.querySelectorAll('[data-rig-type]'));
    var groups = [].slice.call(root.querySelectorAll('[data-rig-group]'));
    var clear  = root.querySelector('[data-rig-clear]');
    if (!types.length) return;

    function hooksOf(key) {
      var g = root.querySelector('[data-rig-group="' + key + '"]');
      return g ? [].slice.call(g.querySelectorAll('[data-rig-variant]')) : [];
    }

    /* Clearing the hook whenever the type changes is deliberate: leaving a
       stale 9/0 selected while the shopper switches TTS -> GZ would silently
       change what they are buying and what it costs. */
    function chooseType(btn) {
      var key = btn && btn.dataset.rigType;
      types.forEach(function (t) {
        var on = t === btn;
        t.setAttribute('aria-checked', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      groups.forEach(function (g) {
        var on = key && g.dataset.rigGroup === key;
        g.hidden = !on;
      });
      root.querySelectorAll('[data-rig-variant]').forEach(function (h) {
        h.setAttribute('aria-checked', 'false');
        h.tabIndex = -1;
      });
      if (key) {
        var hs = hooksOf(key);
        if (hs[0]) hs[0].tabIndex = 0;
      } else if (types[0]) {
        types[0].tabIndex = 0;
      }
      root.classList.remove('is-chosen');
    }

    function chooseHook(btn) {
      if (btn.dataset.rigAvailable !== 'true') return;
      var siblings = [].slice.call(btn.closest('[data-rig-group]').querySelectorAll('[data-rig-variant]'));
      var already = btn.getAttribute('aria-checked') === 'true';
      siblings.forEach(function (h) {
        var on = !already && h === btn;
        h.setAttribute('aria-checked', on ? 'true' : 'false');
        h.tabIndex = on ? 0 : -1;
      });
      if (already && siblings[0]) siblings[0].tabIndex = 0;
      root.classList.toggle('is-chosen', !already);
    }

    function arrows(list) {
      list.forEach(function (el, i) {
        el.addEventListener('keydown', function (e) {
          var k = e.key;
          if (k !== 'ArrowRight' && k !== 'ArrowDown' && k !== 'ArrowLeft' && k !== 'ArrowUp') return;
          e.preventDefault();
          var step = (k === 'ArrowRight' || k === 'ArrowDown') ? 1 : -1;
          list[(i + step + list.length) % list.length].focus();
        });
      });
    }

    types.forEach(function (t) {
      t.addEventListener('click', function () {
        chooseType(t.getAttribute('aria-checked') === 'true' ? null : t);
      });
    });
    arrows(types);

    groups.forEach(function (g) {
      var hs = [].slice.call(g.querySelectorAll('[data-rig-variant]'));
      hs.forEach(function (h) { h.addEventListener('click', function () { chooseHook(h); }); });
      arrows(hs);
    });

    if (clear) clear.addEventListener('click', function () { chooseType(null); });
  }

  /*
    ONE document-level listener, in the CAPTURE phase.

    assets/cart.js binds its own submit handler on `document` (bubble phase) and
    AJAX-adds the form. A form-level listener here therefore did NOT replace it —
    both ran, so the lure was added twice: once by this file's two-item call and
    again by the theme's FormData call. Measured, not guessed: the cart came back
    holding one line at quantity 2.

    Capturing on document runs before the theme's bubble handler, and
    stopPropagation keeps the event from ever reaching it.

    The chosen rig is read out of the DOM at submit time rather than from a
    closure, so this stays correct if the block is re-rendered or appears twice.
  */
  function onSubmit(e) {
    var form = e.target.closest && e.target.closest('form[action*="/cart/add"]');
    if (!form) return;

    var chosen = document.querySelector('[data-rig-addon] [data-rig-variant][aria-checked="true"]');
    if (!chosen) return;                          // no rig picked: theme behaves normally

    var idField = form.querySelector('[name="id"]');
    if (!idField || !idField.value) return;       // let the form report its own error

    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();

    var qtyField = form.querySelector('[name="quantity"]');
    var qty = qtyField ? (parseInt(qtyField.value, 10) || 1) : 1;
    var btn = form.querySelector('[data-ef-add]') || form.querySelector('[type="submit"]');
    if (btn) btn.disabled = true;

    /* `_fitting` marks this rig line as one the shop is fitting. The cart reads
       it to decide how many fittings to charge for, and an underscore keeps it
       private — Shopify hides it at checkout and cart-line.liquid skips it.

       A rig bought from its own product page carries no such property, so it is
       charged no fitting. That is also why the two never merge into one line. */
    var items = [
      {id: Number(idField.value), quantity: qty},
      {id: Number(chosen.dataset.rigVariant), quantity: qty, properties: {_fitting: 'yes'}}
    ];

    /* The mandatory fitting fee, as its own line — one per rig, so it tracks
       the rig quantity rather than the order. It goes in THIS array and not a
       follow-up POST for the same reason the rig does: a second call can
       half-succeed and leave a fitted lure in the cart with nothing charged
       for the fitting.

       The id comes off the block, which only emits it when the fee product
       resolved and had an available variant. No attribute means no fee line,
       and the displayed prices carried no fee either — the two stay in step. */
    var host = chosen.closest('[data-rig-addon]');
    var feeId = host && host.dataset.rigFeeVariant;
    if (feeId) items.push({id: Number(feeId), quantity: qty, properties: {_fee: 'yes'}});

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
      body: JSON.stringify({items: items})
    })
      .then(function (r) { return r.json().then(function (j) { return {ok: r.ok, body: j}; }); })
      .then(function (res) {
        if (!res.ok) throw new Error((res.body && res.body.description) || 'add failed');
        /* The theme's drawer refresh/open are private to cart.js, so go to the
           cart page — predictable, and the shopper sees both lines together. */
        window.location.href = '/cart';
      })
      .catch(function (err) {
        if (btn) btn.disabled = false;
        console.error('[rig-addon]', err);
        form.submit();                            // never strand the shopper
      });
  }

  document.addEventListener('submit', onSubmit, true);   // true = capture

  function boot() {
    document.querySelectorAll('[data-rig-addon]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
